import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TablePagination from '@mui/material/TablePagination';
import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useRules } from '../context/RuleContext';

function getFeeDisplay(rule) {
  if (rule.suppress === 'true' || rule.suppress === true) {
    return <Chip label="SUPPRESS" color="error" size="small" />;
  }
  const parts = [];
  if (rule.feeAmount) parts.push(rule.feeAmount);
  if (rule.feePercent !== undefined && rule.feePercent !== null && rule.feePercent !== '') {
    parts.push(`${rule.feePercent}%`);
  }
  if (rule.oncePerBookingFeeAmount) parts.push(`OPB: ${rule.oncePerBookingFeeAmount}`);

  const text = parts.join(' + ');
  const isDiscount = (rule.feePercent && parseFloat(rule.feePercent) < 0) ||
    (rule.feeAmount && rule.feeAmount.includes('-'));

  return <Chip label={text || 'N/A'} color={isDiscount ? 'warning' : 'success'} size="small" variant="outlined" />;
}

function GroupMembers({ title, members, highlight }) {
  return (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{title} — {members.length}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {members.map(m => (
          <Chip key={m} label={m} size="small"
            sx={{ height: 20, fontSize: 11, color: 'white', bgcolor: m === highlight ? 'warning.main' : 'rgba(255,255,255,0.15)', fontWeight: m === highlight ? 700 : 400 }} />
        ))}
      </Box>
    </Box>
  );
}

export default function FeeRules({ onEditRule, onCopyRule }) {
  const { feeRules, deleteFeeRule, carrierGroups } = useRules();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Advanced filters
  const [filterCarrier, setFilterCarrier] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterDestination, setFilterDestination] = useState('');
  const [filterCabin, setFilterCabin] = useState('');
  const [filterTripType, setFilterTripType] = useState('');
  const [filterPriceSource, setFilterPriceSource] = useState('');
  const [filterInterline, setFilterInterline] = useState('');
  const [filterDateStatus, setFilterDateStatus] = useState('');

  const clearFilters = () => {
    setSearch('');
    setFilter('all');
    setFilterCarrier('');
    setFilterOrigin('');
    setFilterDestination('');
    setFilterCabin('');
    setFilterTripType('');
    setFilterPriceSource('');
    setFilterInterline('');
    setFilterDateStatus('');
  };

  const carrierGroupOf = (ref) => carrierGroups.find(g => `U:${g.tableNum}` === ref);

  const filteredRules = useMemo(() => {
    let rules = [...feeRules];
    // True when the rule's carrier is a group (U:N) that contains the airline code, e.g. AC -> U:33
    const groupHasCarrier = (rule, code) => {
      const group = carrierGroups.find(g => `U:${g.tableNum}` === rule.carrier);
      return !!group && group.carriers.some(c => c.toUpperCase() === code);
    };

    // Basic type filter
    if (filter === 'fee') {
      rules = rules.filter(r => {
        if (r.suppress === 'true' || r.suppress === true) return false;
        if (r.feePercent && parseFloat(r.feePercent) > 0) return true;
        if (r.feeAmount && !r.feeAmount.includes('-')) return true;
        return false;
      });
    } else if (filter === 'discount') {
      rules = rules.filter(r => {
        if (r.feePercent && parseFloat(r.feePercent) < 0) return true;
        if (r.feeAmount && r.feeAmount.includes('-')) return true;
        return false;
      });
    } else if (filter === 'suppress') {
      rules = rules.filter(r => r.suppress === 'true' || r.suppress === true);
    }

    // Advanced filters
    if (filterCarrier) {
      const fc = filterCarrier.trim().toUpperCase();
      rules = rules.filter(r => r.carrier && (r.carrier.toUpperCase().includes(fc) || groupHasCarrier(r, fc)));
    }
    if (filterOrigin) {
      const fo = filterOrigin.toUpperCase();
      rules = rules.filter(r => r.origin && r.origin.toUpperCase().includes(fo));
    }
    if (filterDestination) {
      const fd = filterDestination.toUpperCase();
      rules = rules.filter(r => r.destination && r.destination.toUpperCase().includes(fd));
    }
    if (filterCabin) {
      rules = rules.filter(r => r.cabinClass === filterCabin);
    }
    if (filterTripType) {
      rules = rules.filter(r => r.tripType === filterTripType);
    }
    if (filterPriceSource) {
      rules = rules.filter(r => r.priceSource === filterPriceSource);
    }
    if (filterInterline) {
      rules = rules.filter(r => r.interline === filterInterline);
    }
    if (filterDateStatus) {
      const now = new Date();
      if (filterDateStatus === 'expired') {
        rules = rules.filter(r => r.travelDateDiscontinue && new Date(r.travelDateDiscontinue) < now);
      } else if (filterDateStatus === 'active') {
        rules = rules.filter(r => !r.travelDateDiscontinue || new Date(r.travelDateDiscontinue) >= now);
      } else if (filterDateStatus === 'expiring') {
        const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        rules = rules.filter(r => r.travelDateDiscontinue && new Date(r.travelDateDiscontinue) >= now && new Date(r.travelDateDiscontinue) <= thirtyDays);
      }
    }

    // Text search
    if (search) {
      const s = search.trim().toLowerCase();
      rules = rules.filter(r =>
        (r.carrier && r.carrier.toLowerCase().includes(s)) ||
        (r.carrier && groupHasCarrier(r, s.toUpperCase())) ||
        (r.origin && r.origin.toLowerCase().includes(s)) ||
        (r.destination && r.destination.toLowerCase().includes(s)) ||
        (r.cabinClass && r.cabinClass.toLowerCase().includes(s)) ||
        String(r.sequenceNum).includes(s)
      );
    }

    return rules;
  }, [feeRules, carrierGroups, search, filter, filterCarrier, filterOrigin, filterDestination, filterCabin, filterTripType, filterPriceSource, filterInterline, filterDateStatus]);

  const handleDelete = (seqNum) => {
    if (window.confirm(`Are you sure you want to delete rule #${seqNum}?`)) {
      deleteFeeRule(seqNum);
    }
  };

  const activeFilterCount = [filterCarrier, filterOrigin, filterDestination, filterCabin, filterTripType, filterPriceSource, filterInterline, filterDateStatus].filter(Boolean).length;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Fee Rules ({feeRules.length})</Typography>

      {/* Search & Basic Filter */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by carrier, origin, destination..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filter} label="Type" onChange={(e) => { setFilter(e.target.value); setPage(0); }}>
              <MenuItem value="all">All Rules</MenuItem>
              <MenuItem value="fee">Fees (+)</MenuItem>
              <MenuItem value="discount">Discounts (-)</MenuItem>
              <MenuItem value="suppress">Suppression</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant={showAdvancedFilters ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            size="small"
          >
            Advanced Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
          {activeFilterCount > 0 && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} color="error">
              Clear All
            </Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredRules.length} of {feeRules.length} rules
          </Typography>
        </Box>

        {/* Advanced Filters Panel */}
        <Collapse in={showAdvancedFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={3}>
                <TextField fullWidth size="small" label="Carrier" value={filterCarrier} onChange={e => { setFilterCarrier(e.target.value); setPage(0); }} placeholder="e.g. AI, U:9" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <TextField fullWidth size="small" label="Origin" value={filterOrigin} onChange={e => { setFilterOrigin(e.target.value); setPage(0); }} placeholder="e.g. N:US" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <TextField fullWidth size="small" label="Destination" value={filterDestination} onChange={e => { setFilterDestination(e.target.value); setPage(0); }} placeholder="e.g. N:IN" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cabin Class</InputLabel>
                  <Select value={filterCabin} label="Cabin Class" onChange={e => { setFilterCabin(e.target.value); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ECONOMY">Economy</MenuItem>
                    <MenuItem value="BUSINESS">Business</MenuItem>
                    <MenuItem value="PREMIUM">Premium</MenuItem>
                    <MenuItem value="FIRST">First</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Trip Type</InputLabel>
                  <Select value={filterTripType} label="Trip Type" onChange={e => { setFilterTripType(e.target.value); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ONE_WAY">One Way</MenuItem>
                    <MenuItem value="ROUND_TRIP">Round Trip</MenuItem>
                    <MenuItem value="OPEN_JAW">Open Jaw</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Price Source</InputLabel>
                  <Select value={filterPriceSource} label="Price Source" onChange={e => { setFilterPriceSource(e.target.value); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="GDS">GDS</MenuItem>
                    <MenuItem value="WEBSITE">Website</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Interline</InputLabel>
                  <Select value={filterInterline} label="Interline" onChange={e => { setFilterInterline(e.target.value); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Status</InputLabel>
                  <Select value={filterDateStatus} label="Date Status" onChange={e => { setFilterDateStatus(e.target.value); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expiring">Expiring Soon</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Card>

      {/* Rules Table */}
      <Card>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Seq#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Carrier</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fee/Discount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Trip Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cabin</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Travel Dates</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Price Range</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Interline</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {feeRules.length === 0 ? 'No rules loaded. Upload an XML file to view rules.' : 'No rules match your filter criteria.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rule) => {
                  const cGroup = rule.carrier ? carrierGroupOf(rule.carrier) : null;
                  // Airline code being searched: Carrier filter takes priority over the search box
                  const code = (filterCarrier || search).trim().toUpperCase();
                  const viaGroup = !!cGroup && !!code && !rule.carrier.toUpperCase().includes(code)
                    && cGroup.carriers.some(c => c.toUpperCase() === code);
                  return (
                  <TableRow key={rule.sequenceNum} hover>
                    <TableCell><strong>{rule.sequenceNum}</strong></TableCell>
                    <TableCell>
                      {cGroup ? (
                        <Tooltip arrow placement="right"
                          title={<GroupMembers title={`Carrier group ${rule.carrier}`} members={cGroup.carriers} highlight={code} />}>
                          <Chip label={viaGroup ? `${rule.carrier} (has ${code})` : rule.carrier} size="small"
                            color={viaGroup ? 'warning' : 'default'} variant="outlined" sx={{ cursor: 'help' }} />
                        </Tooltip>
                      ) : (
                        <Chip label={rule.carrier} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{rule.origin || '—'}</TableCell>
                    <TableCell>{rule.destination || '—'}</TableCell>
                    <TableCell>{getFeeDisplay(rule)}</TableCell>
                    <TableCell>
                      {rule.tripType ? <Chip label={rule.tripType.replace(/_/g, ' ')} size="small" variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell>{rule.cabinClass || '—'}</TableCell>
                    <TableCell>
                      {rule.travelDateEffective || rule.travelDateDiscontinue ? (
                        <Typography variant="caption">
                          {rule.travelDateEffective || '...'} → {rule.travelDateDiscontinue || '...'}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {rule.minPrice || rule.maxPrice ? (
                        <Typography variant="caption">
                          {rule.minPrice || '0'} - {rule.maxPrice || '∞'}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {rule.interline !== undefined && rule.interline !== null && rule.interline !== '' ? (
                        <Chip label={rule.interline === 'false' ? 'No' : 'Yes'} size="small" color={rule.interline === 'false' ? 'default' : 'info'} />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{rule.priceSource || '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => onEditRule(rule)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy & Insert">
                        <IconButton size="small" color="secondary" onClick={() => onCopyRule(rule)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(rule.sequenceNum)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredRules.length > 0 && (
          <TablePagination
            component="div"
            count={filteredRules.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>
    </Box>
  );
}
